import { Navigate } from '@starsoft/common/components';
import { CommonModalsKey, WithdrawStep } from '@starsoft/common/enums';
import {
  useModal,
  useModalNavigation,
  useSearchParams,
  useToggle,
  useWithdrawForm,
  useWithdrawFormState,
  useWithdrawSearchParams,
} from '@starsoft/common/hooks';
import { CoinType, SettingLayoutComponentId } from '@starsoft/common/models';
import { AnimatePresence, motion } from 'framer-motion';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSendWithdrawalCode } from '../../../../../api';
import useWithdrawal from '../../../../../api/withdrawals/mutations/useWithdraw';
import { WithdrawFormCreateDto } from '../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import useTransactionsTabNavigation from '../../../../../hooks/useTransactionsTabNavigation';
import TransactionModalsLayoutFooter from '../../../Common/Layout/Footer';
import TransactionsModalsLayoutHeader from '../../../Common/Layout/Header';
import TransactionModalsContentLayoutTabs from '../../../Common/Layout/Tabs';
import UserPaymentDetailsForm from '../../../Common/Layout/UserPaymentDetails/Form';
import { fadeInRight } from '../../../Common/variants';
import { TransactionModalsWithdrawContentLayoutProps } from './props';
import SecurityDrawer from './SecurityDrawer';
import styles from './styles.module.scss';

/**
 * TransactionModalsWithdrawContentLayout Component
 * Main layout component for the withdrawal modal that handles the withdrawal flow including form submission,
 * navigation between steps, and security verification.
 *
 * Logic scope:
 * - Manages withdrawal form state and validation
 * - Handles navigation between different withdrawal steps
 * - Controls security drawer visibility
 * - Manages form submission based on current step
 * - Handles coin type validation and defaults
 * - Provides layout structure with header, content area and footer
 *
 * @component
 * @param {PropsWithChildren} props - React children to be rendered within the layout
 * @returns {JSX.Element} A form container with withdrawal flow UI components
 *
 * @example
 * <TransactionModalsWithdrawContentLayout>
 *   <WithdrawFormContent />
 * </TransactionModalsWithdrawContentLayout>
 */
export default function TransactionModalsWithdrawContentLayout({
  children,
  originId,
  version,
}: PropsWithChildren<TransactionModalsWithdrawContentLayoutProps>) {
  const [isDrawerOpen, toggleDrawer] = useToggle();
  const { onClose } = useModal(CommonModalsKey.Withdraw);
  const {
    transactionStep,
    coinType,
    handleSetWithdrawSearchParams,
    handleDeleteWithdrawSearchParams,
  } = useWithdrawSearchParams();

  const { getModalLink } = useModalNavigation();
  const search: URLSearchParams = useSearchParams();
  const { coinId } = useWithdrawSearchParams();
  const disableStyles =
    transactionStep == WithdrawStep.Form ||
    transactionStep === WithdrawStep.UserPaymentDetails;

  const methods = useForm<WithdrawFormCreateDto>({
    defaultValues: {
      amount: 0,
      coinId: coinId?.toString(),
    },
  });

  const { handleSubmit, watch, setValue } = methods;
  const { setTab } = useTransactionsTabNavigation({ setValue });
  const amount = watch('amount');
  const emailId: bigint = watch('crypto.emailId');

  const {
    feeQuery: { isLoadingFee, fee },
    formError,
    isFormLoading,
    accountByCoinQuery: { currentAccount },
    coinOptions,
    isValidCoinId,
  } = useWithdrawForm(watch, setValue);
  const isSubmitDisabled: boolean = useWithdrawFormState({
    watch,
    setValue,
  });

  const { isLoading: isLoadingCreateWithdrawal, withdraw } = useWithdrawal({
    onSuccess: toggleDrawer,
  });
  const { sendWithdrawalCode } = useSendWithdrawalCode();

  /**
   * Handles navigation when back button is clicked
   * Manages step transitions based on coin type and current step
   *
   * @returns {void}
   *
   * @example
   * // When on confirmation step with crypto
   * handleBackButton() // Returns to form step
   *
   * // When on first step
   * handleBackButton() // Closes modal
   */
  const handleBackButton = useCallback(() => {
    if (!!search.get('user-payment')) {
      handleDeleteWithdrawSearchParams('user-payment');
      return;
    }
    const paths = Object.values(WithdrawStep);
    const currentIndex = paths.findIndex(path => path == transactionStep);

    if (currentIndex == 0) {
      onClose();
    }

    let targetPath = paths[currentIndex - 1];

    if (
      coinType == CoinType.Crypto &&
      targetPath == WithdrawStep.UserPaymentDetails
    ) {
      targetPath = WithdrawStep.Form;
    }

    if (coinType == CoinType.Fiat && targetPath != WithdrawStep.Form) {
      targetPath = WithdrawStep.Form;
    }

    handleSetWithdrawSearchParams('step', targetPath);
  }, [
    transactionStep,
    handleSetWithdrawSearchParams,
    onClose,
    coinType,
    search,
    handleDeleteWithdrawSearchParams,
  ]);

  /**
   * Handles form submission based on current withdrawal step
   * Controls flow between steps and triggers appropriate actions
   *
   * @param {WithdrawFormCreateDto} data - Form data for withdrawal
   * @returns {void}
   *
   * @example
   * onSubmit({
   *   amount: 100,
   *   coinId: "1",
   *   emailId: 123n
   * })
   */
  function onSubmit(data: WithdrawFormCreateDto) {
    switch (transactionStep) {
      case WithdrawStep.Form:
        switch (coinType) {
          case CoinType.Fiat:
            handleSetWithdrawSearchParams(
              'step',
              WithdrawStep.UserPaymentDetails,
            );
            break;
          case CoinType.Crypto:
            handleSetWithdrawSearchParams('step', WithdrawStep.Confirmation);
            break;
        }
        break;
      case WithdrawStep.Confirmation:
        sendWithdrawalCode({
          emailId,
        });
        handleSetWithdrawSearchParams('step', WithdrawStep.ConfirmationCode);
        break;
      case WithdrawStep.ConfirmationCode:
      case WithdrawStep.UserPaymentDetails:
        if (!isDrawerOpen) {
          toggleDrawer();
          return;
        }

        withdraw(data);
        break;
      case WithdrawStep.Response:
        break;
    }
  }

  /**
   * Validates selected coin ID and sets default if invalid
   * Ensures selected coin is available in options
   *
   * @returns {void}
   */
  function handleInvalidCoinId(): void {
    if (isValidCoinId || !coinOptions || coinOptions?.length === 0) {
      return;
    }

    handleSetWithdrawSearchParams('coin-id', coinOptions[0].id as never);
  }

  useEffect(handleInvalidCoinId, [
    coinOptions,
    isValidCoinId,
    handleSetWithdrawSearchParams,
  ]);

  if (
    ![WithdrawStep.Form, WithdrawStep.Response].includes(transactionStep) &&
    (!amount || amount == 0)
  ) {
    search.set('step', WithdrawStep.Form);
    search.delete('amount');
    search.delete('method-id');
    return (
      <Navigate
        to={`${getModalLink(CommonModalsKey.Withdraw, `?${search.toString()}`)}`}
        replace
      />
    );
  }

  return (
    <div
      className={`${styles.container} ${version === SettingLayoutComponentId.ModalV2?.toString() ? styles['container--v2'] : ''}`}
    >
      <UserPaymentDetailsForm />
      <TransactionsModalsLayoutHeader
        handleBackButton={handleBackButton}
        onClose={onClose}
        version={version}
        transactionStep={transactionStep}
      />
      <TransactionModalsContentLayoutTabs
        currentTab={coinType}
        onTabChange={setTab}
        version={version}
        originId={originId}
        hidden={![WithdrawStep.Form].includes(transactionStep)}
      />
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          id="withdraw"
          className={styles.container__content}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={transactionStep}
              variants={fadeInRight}
              initial="hidden"
              animate="show"
              className={`${styles.container__content__wrapper}  ${disableStyles ? styles['container__content__wrapper--disable-styles'] : ''}`}
            >
              {children}
            </motion.div>
          </AnimatePresence>
          <TransactionModalsLayoutFooter
            error={formError}
            feeOptions={{
              fee,
              isLoadingFee,
            }}
            coin={currentAccount?.coin}
            amount={amount}
            coinType={coinType}
            isLoading={isFormLoading}
            i18nNamespace="withdraw"
            transactionStep={transactionStep}
            isSubmitButtonDisabled={isSubmitDisabled}
            isLoadingSubmit={isLoadingCreateWithdrawal}
          />
          <SecurityDrawer
            onClose={toggleDrawer}
            open={isDrawerOpen}
            isLoading={isLoadingCreateWithdrawal}
          />
        </form>
      </FormProvider>
    </div>
  );
}
